import type { IssueStatus } from "@/domains/pm/types";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { resolveStatus } from "@/domains/pm/statuses/statusCatalogue";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface StatusBadgeProps {
  status: IssueStatus;
  /** The issue's building project. Two projects may define the same key with
   *  different colours, so pass it wherever it is to hand; without it the key
   *  resolves against every project's statuses, first by order. */
  buildingProjectId?: number | null;
  /** `sm` (default) — compact list/menu density. `lg` — ~35% larger for the
   *  expanded issue panel's meta bar. */
  size?: "sm" | "lg";
  /** `pill` wraps the dot + label in the status' tinted background — used by
   *  the issue detail panel's header, where status is the headline attribute
   *  (WHA-3810). Defaults to the bare dot + coloured label. */
  variant?: "bare" | "pill" | "dot";
}

export function StatusBadge({
  status,
  buildingProjectId,
  size = "sm",
  variant = "bare",
}: StatusBadgeProps) {
  const { catalogue } = useStatusCatalogue();
  // Always a descriptor, never undefined: a status row can be deleted while
  // this page is open, and dereferencing .dotColor on the miss used to throw.
  const config = resolveStatus(catalogue, buildingProjectId, status);
  const label = config.label;

  // `dot` drops the label for dense lists. Colour alone is not a status
  // anybody can read, so the dot carries the label as its accessible name and
  // repeats it in a tooltip for a hovering mouse.
  if (variant === "dot") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={label}
            className={cn(
              "inline-block shrink-0 rounded-full",
              size === "lg" ? "h-2.5 w-2.5" : "h-2 w-2"
            )}
            style={{ backgroundColor: config.dotColor }}
          />
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }

  const textClass = size === "lg" ? "gap-1.5 text-[15px]" : "gap-1 text-[11px]";
  const dotClass = size === "lg" ? "h-2 w-2" : "h-1.5 w-1.5";
  const pillClass =
    variant === "pill"
      ? size === "lg"
        ? "rounded-full px-2.5 py-1"
        : "rounded-full px-2 py-0.5"
      : "";

  return (
    <span
      className={`inline-flex items-center whitespace-nowrap font-medium ${textClass} ${pillClass}`}
      style={{
        color: config.textColor,
        backgroundColor: variant === "pill" ? config.bgColor : undefined,
      }}
    >
      <span className={`${dotClass} rounded-full`} style={{ backgroundColor: config.dotColor }} />
      {label}
    </span>
  );
}
